import logger from '@/utils/logger';
/**
 * Utilitaires de mapping des données chauffeurs
 *
 * Gère la conversion entre les formats frontend (camelCase) et backend (snake_case)
 * pour assurer la cohérence des données à travers l'application.
 *
 * @module utils/driverMapper
 */

/**
 * Mappe les données d'un chauffeur du format frontend (camelCase) vers le format backend (snake_case)
 * pour la création d'un nouveau chauffeur
 *
 * @param {Object} driverData - Données du chauffeur au format frontend
 * @param {string} driverData.firstName - Prénom du chauffeur
 * @param {string} driverData.lastName - Nom du chauffeur
 * @param {string} driverData.email - Email du chauffeur
 * @param {string} driverData.phone - Numéro de téléphone
 * @param {string} [driverData.status='active'] - Statut du chauffeur
 * @param {string} [driverData.licenseNumber] - Numéro de permis
 * @param {string} [driverData.license_expiry] - Date d'expiration du permis
 * @param {string} [driverData.hireDate] - Date d'embauche
 * @param {string} [driverData.dateOfBirth] - Date de naissance
 * @param {string} [driverData.gender] - Genre (male | female)
 * @param {string} [driverData.address] - Adresse
 * @param {number|null} [driverData.homeLatitude] - Latitude du domicile
 * @param {number|null} [driverData.homeLongitude] - Longitude du domicile
 * @param {string} [driverData.notes] - Notes additionnelles
 * @returns {Object} Données mappées au format backend
 *
 * @example
 * const frontendData = {
 *   firstName: 'Ahmed',
 *   lastName: 'Benali',
 *   email: 'ahmed@example.com',
 *   phoneNumber: '+212600000000'
 * };
 * const backendData = mapDriverToBackend(frontendData);
 * // { first_name: 'Ahmed', last_name: 'Benali', email: 'ahmed@example.com', phone: '+212600000000' }
 */
export const mapDriverToBackend = (driverData) => {
  const backendData = {
    // Champs de base (toujours requis)
    first_name: driverData.firstName || driverData.first_name,
    last_name: driverData.lastName || driverData.last_name,
    email: driverData.email,
    phone: driverData.phoneNumber || driverData.phone,
    status: driverData.status || 'active',

    // Profil saisi dans le formulaire (DriverFormModal) — sans ces clés, la
    // date de naissance, le genre, l'adresse et surtout les coordonnées GPS du
    // domicile (offre basic : bus garé chez le chauffeur) étaient perdus.
    date_of_birth: driverData.dateOfBirth || driverData.date_of_birth,
    gender: driverData.gender,
    address: driverData.address,
    // `!== undefined` et pas `??` : `null` est une valeur voulue (coordonnée vidée)
    home_latitude: driverData.homeLatitude !== undefined ? driverData.homeLatitude : driverData.home_latitude,
    home_longitude: driverData.homeLongitude !== undefined ? driverData.homeLongitude : driverData.home_longitude,

    // Champs permis (optionnels)
    license_number: driverData.licenseNumber || driverData.license_number,
    license_expiry: driverData.license_expiry,

    // Champs professionnels (optionnels)
    hire_date: driverData.hireDate || driverData.hire_date,
    notes: driverData.notes,
  };

  // Nettoyer les champs undefined ou vides pour éviter les erreurs de validation
  // (`null` est conservé : c'est une remise à zéro explicite, ex. latitude vidée)
  Object.keys(backendData).forEach((key) => {
    if (backendData[key] === undefined || backendData[key] === '') {
      delete backendData[key];
    }
  });

  return backendData;
};

/**
 * Mappe les données d'un chauffeur du format frontend (camelCase) vers le format backend (snake_case)
 * pour la mise à jour d'un chauffeur existant
 *
 * Gère également la conversion des champs numériques (chaîne vide → null)
 * et supprime les propriétés camelCase redondantes.
 *
 * @param {Object} driverData - Données du chauffeur au format frontend
 * @returns {Object} Données mappées au format backend
 *
 * @example
 * const frontendData = {
 *   firstName: 'Ahmed',
 *   lastName: 'Benali',
 *   age: '',
 *   salary: '5000'
 * };
 * const backendData = mapDriverUpdateToBackend(frontendData);
 * // { first_name: 'Ahmed', last_name: 'Benali', age: null, salary: 5000 }
 */
export const mapDriverUpdateToBackend = (driverData) => {
  const backendData = {
    ...driverData,
    // Mapping des champs camelCase vers snake_case
    first_name: driverData.firstName || driverData.first_name,
    last_name: driverData.lastName || driverData.last_name,
    license_number: driverData.license?.number || driverData.licenseNumber || driverData.license_number,
    phone: driverData.phoneNumber || driverData.phone,
    assigned_bus_id: driverData.assignedBusId || driverData.assigned_bus_id,
    assigned_route_id: driverData.assignedRouteId || driverData.assigned_route_id,
    emergency_contact: driverData.emergencyContact || driverData.emergency_contact,
    date_of_birth: driverData.dateOfBirth || driverData.date_of_birth,
    hire_date: driverData.hireDate || driverData.hire_date,

    // Conversion des champs numériques : chaîne vide → null
    max_hours_per_day: driverData.max_hours_per_day === '' ? null : driverData.max_hours_per_day,
    max_hours_per_week: driverData.max_hours_per_week === '' ? null : driverData.max_hours_per_week,
    rating: driverData.rating === '' ? null : driverData.rating,
    total_ratings: driverData.total_ratings === '' ? null : driverData.total_ratings,
    age: driverData.age === '' ? null : driverData.age,
    salary: driverData.salary === '' ? null : driverData.salary,
    seniority: driverData.seniority === '' ? null : driverData.seniority,

    // Supprimer les propriétés camelCase pour éviter la confusion
    firstName: undefined,
    lastName: undefined,
    licenseNumber: undefined,
    phoneNumber: undefined,
    assignedBusId: undefined,
    assignedRouteId: undefined,
    emergencyContact: undefined,
    dateOfBirth: undefined,
    hireDate: undefined,
    license: undefined, // Supprimer l'objet license imbriqué (mappé vers license_number)
    id: undefined, // Supprimer l'ID personnalisé - laisser PostgreSQL générer l'UUID
    driverId: undefined, // Supprimer aussi driverId du formulaire
  };

  // Nettoyer les champs undefined
  Object.keys(backendData).forEach((key) => {
    if (backendData[key] === undefined) {
      delete backendData[key];
    }
  });

  return backendData;
};

/**
 * Mappe les données d'un chauffeur du format backend (snake_case) vers le format frontend (camelCase)
 *
 * Utilisé lors de la récupération des données depuis l'API pour les afficher dans le frontend.
 *
 * @param {Object} backendData - Données du chauffeur au format backend
 * @returns {Object} Données mappées au format frontend
 *
 * @example
 * const backendData = {
 *   first_name: 'Ahmed',
 *   last_name: 'Benali',
 *   license_number: 'A123456'
 * };
 * const frontendData = mapDriverToFrontend(backendData);
 * // { firstName: 'Ahmed', lastName: 'Benali', licenseNumber: 'A123456' }
 */
export const mapDriverToFrontend = (backendData) => {
  if (!backendData) return null;

  return {
    ...backendData,
    // Mapping des champs snake_case vers camelCase
    firstName: backendData.first_name,
    lastName: backendData.last_name,
    phoneNumber: backendData.phone,
    licenseNumber: backendData.license_number,
    licenseExpiry: backendData.license_expiry,
    hireDate: backendData.hire_date,
    emergencyContact: backendData.emergency_contact,
    dateOfBirth: backendData.date_of_birth,
    assignedBusId: backendData.assigned_bus_id,
    assignedRouteId: backendData.assigned_route_id,
    maxHoursPerDay: backendData.max_hours_per_day,
    maxHoursPerWeek: backendData.max_hours_per_week,
    totalRatings: backendData.total_ratings,
  };
};

/**
 * Log les informations de mapping pour le débogage
 *
 * @param {string} operation - Type d'opération ('create' ou 'update')
 * @param {Object} original - Données originales (frontend)
 * @param {Object} mapped - Données mappées (backend)
 * @param {number} [fieldsCount] - Nombre de champs mappés
 */
export const logMapping = (operation, original, mapped, fieldsCount) => {
  logger.debug(`🔄 Mapping frontend → backend (${operation}):`, {
    original,
    mapped,
    ...(fieldsCount !== undefined && { fieldsCount }),
  });
};
